import styled from '@emotion/styled';
import React from 'react';

import { color, font, shadow } from 'src/shared/styles/variables';
import { colors as dsColors } from '@modernhealth/vital-foundation';

interface InputFieldProps {
    invalid: boolean;
}

const InputField = styled.input<InputFieldProps>(
    props => ({
        width: '100%',
        borderRadius: 5,
        padding: 10,
        border: `1px solid ${props.invalid ? color.brinkPink : color.linkWaterLight} !important`,
        boxShadow: ` ${shadow.regularShadow} !important`,
        color: `${color.fiord} !important`,
        '::placeholder': {
            color: color.casper,
        },
    }),
    font.heavy(font.size.base),
);

const TextField = styled.textarea<InputFieldProps>(
    props => ({
        width: '100%',
        height: 150,
        borderRadius: 5,
        padding: 10,
        border: `1px solid ${props.invalid ? color.brinkPink : color.linkWaterLight} !important `,
        boxShadow: `${shadow.regularShadow} !important `,
        color: color.fiord,
        '::placeholder': {
            color: color.baliHai,
            ...font.light(font.size.base),
        },
        '&:focus-visible': {
            boxShadow: `0px 0px 0px 4px ${dsColors.cyan20} !important`,
            outline: 'none',
        },
    }),
    font.heavy(font.size.base),
);

const InputLabel = styled.label(
    {
        display: 'block',
        marginTop: 20,
        marginBottom: 8,
        color: `${color.steelGray} !important`,
    },
    font.heavy(font.size.base),
);

const AsteriskLabel = styled.span({
    color: color.wildWatermelon,
});

interface ErrorMessageProps {
    visible: boolean;
}

const ErrorMessage = styled.span<ErrorMessageProps>(
    ({ visible }) => ({
        display: 'block',
        marginTop: visible ? 8 : 0,
        color: color.wildWatermelon,
        transition: '.2s ease-in-out margin-top',
    }),
    font.book(font.size.base),
);

interface Props {
    fieldId: string;
    value: string;
    onChange: (value: string) => void;
    onBlur?: (e: React.FocusEvent) => void;
    error?: string;
    testID?: string;
    type: 'text' | 'textarea';
    label?: string;
    placeholder?: string;
    readOnly?: boolean;
    showAsterisk?: boolean;
    errorMessageTestID?: string;
}

/**
 * @deprecated
 *
 * Use `<Input>` from `@modernhealth/vital-components`
 */
export const Input: React.FC<Props> = ({
    fieldId,
    label,
    type,
    value,
    onChange,
    onBlur,
    placeholder = '',
    error = '',
    testID = '',
    readOnly,
    errorMessageTestID,
    showAsterisk,
}) => {
    return (
        <>
            {label && (
                <InputLabel htmlFor={fieldId}>
                    {label}
                    {showAsterisk ? <AsteriskLabel> *</AsteriskLabel> : null}
                </InputLabel>
            )}

            {type === 'textarea' ? (
                <TextField
                    id={fieldId}
                    name={fieldId}
                    value={value}
                    placeholder={placeholder}
                    invalid={!!error}
                    onChange={e => onChange(e.target.value)}
                    onBlur={onBlur}
                    data-testid={testID}
                />
            ) : (
                <InputField
                    type={type}
                    id={fieldId}
                    name={fieldId}
                    value={value}
                    placeholder={placeholder}
                    invalid={!!error}
                    onChange={e => onChange(e.target.value)}
                    onBlur={onBlur}
                    readOnly={readOnly}
                    data-testid={testID}
                />
            )}

            <ErrorMessage visible={!!error} data-testid={errorMessageTestID}>
                {error}
            </ErrorMessage>
        </>
    );
};
